import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import {
  Shuffle as ShuffleIcon,
  RestartAlt as ClearIcon,
  BackspaceOutlined as BackspaceIcon,
  Dialpad as DialpadIcon,
  CheckCircleOutline as CheckIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";

const KEYPAD_ROWS = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  ["CLEAR", 0, "DELETE"],
];

const MissileKeypadDeck = ({
  pickNumbers = 3,
  selectedNumber = "",
  onSelectNumber,
  onQuickPick,
  onClear,
}) => {
  const maxDigits = Math.max(1, Number(pickNumbers) || 3);
  const currentLength = selectedNumber.length;
  const isFull = currentLength >= maxDigits;

  const handleKeypadPress = (val) => {
    if (val === "DELETE") {
      if (selectedNumber.length > 0) {
        onSelectNumber(selectedNumber.slice(0, -1));
      }
    } else if (val === "CLEAR") {
      onClear();
    } else {
      if (currentLength < maxDigits) {
        onSelectNumber(`${selectedNumber}${val}`);
      }
    }
  };

  // Keyboard events listener for typing target code
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is inside an input/textarea/select
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      ) {
        return;
      }

      if (/^[0-9]$/.test(e.key)) {
        if (selectedNumber.length < maxDigits) {
          onSelectNumber(`${selectedNumber}${e.key}`);
        }
      } else if (e.key === "Backspace") {
        if (selectedNumber.length > 0) {
          onSelectNumber(selectedNumber.slice(0, -1));
        }
      } else if (e.key === "Escape") {
        onClear();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedNumber, maxDigits, onSelectNumber, onClear]);

  // Construct target slot items
  const slots = useMemo(
    () =>
      Array.from({ length: maxDigits }, (_, index) => {
        const char = selectedNumber[index];
        const isFilled = char !== undefined;
        const isActive = index === currentLength;

        return {
          index,
          char: char || "",
          isFilled,
          isActive,
        };
      }),
    [maxDigits, selectedNumber, currentLength]
  );

  // Dynamic responsive slot dimensions based on maxDigits count
  const slotDimensions = useMemo(() => {
    if (maxDigits <= 3) {
      return {
        width: { xs: 46, sm: 58, md: 66 },
        height: { xs: 54, sm: 66, md: 74 },
        fontSize: { xs: "1.4rem", sm: "1.9rem", md: "2.15rem" },
        gap: { xs: 1.5, sm: 2 },
        borderRadius: borderRadiusPx.lg,
      };
    }
    if (maxDigits <= 5) {
      return {
        width: { xs: 38, sm: 46, md: 54 },
        height: { xs: 46, sm: 54, md: 62 },
        fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.75rem" },
        gap: { xs: 1, sm: 1.5 },
        borderRadius: borderRadiusPx.md,
      };
    }
    if (maxDigits <= 7) {
      return {
        width: { xs: 30, sm: 36, md: 42 },
        height: { xs: 38, sm: 44, md: 50 },
        fontSize: { xs: "0.95rem", sm: "1.2rem", md: "1.35rem" },
        gap: { xs: 0.75, sm: 1 },
        borderRadius: borderRadiusPx.md,
      };
    }
    // For 8, 9, 10+ digits (e.g. 9-digit jackpot):
    return {
      width: { xs: 24, sm: 30, md: 36 },
      height: { xs: 32, sm: 38, md: 44 },
      fontSize: { xs: "0.8rem", sm: "1rem", md: "1.15rem" },
      gap: { xs: 0.5, sm: 0.75 },
      borderRadius: borderRadiusPx.sm,
    };
  }, [maxDigits]);

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.slate[200]}`,
        bgcolor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
        mb: 3,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {/* Header with Title and Quick Actions */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <DialpadIcon sx={{ color: colors.primary.main }} />
              Target Code Deck
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.slate[500], mt: 0.25 }}
            >
              Enter a combination of <strong>{maxDigits} digits</strong> to calibrate
              the missile launch.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <CommonButton
              variant="amberAction"
              size="small"
              startIcon={<ShuffleIcon fontSize="small" />}
              onClick={onQuickPick}
              label="Random Target"
            />
            <CommonButton
              variant="clearAction"
              size="small"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={onClear}
              disabled={!selectedNumber}
              label="Clear"
            />
          </Stack>
        </Stack>

        {/* Digital Target Slot Boxes */}
        <Box
          sx={{
            bgcolor: colors.slate[900],
            borderRadius: borderRadiusPx.xl,
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${colors.slate[800]}`,
            boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.4)",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: colors.slate[400],
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Launch Coordinates ({currentLength}/{maxDigits})
          </Typography>

          {/* Slots Responsive Container */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: slotDimensions.gap,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              maxWidth: "100%",
              px: { xs: 0.5, sm: 1 },
              boxSizing: "border-box",
            }}
          >
            {slots.map((slot) => (
              <Box
                key={slot.index}
                sx={{
                  width: slotDimensions.width,
                  height: slotDimensions.height,
                  borderRadius: slotDimensions.borderRadius,
                  bgcolor: slot.isFilled
                    ? "rgba(37, 99, 235, 0.18)"
                    : slot.isActive
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(255, 255, 255, 0.03)",
                  border: slot.isFilled
                    ? `2px solid ${colors.primary.main}`
                    : slot.isActive
                    ? `2px dashed ${colors.amber[400]}`
                    : `1px solid ${colors.slate[800]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: slot.isFilled ? "#ffffff" : colors.slate[600],
                  fontSize: slotDimensions.fontSize,
                  fontWeight: 800,
                  fontFamily: "monospace",
                  boxShadow: slot.isFilled
                    ? `0 0 16px ${colors.primary.main}44`
                    : "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  flexShrink: 0,
                  userSelect: "none",
                }}
              >
                {slot.isFilled ? slot.char : "•"}
              </Box>
            ))}
          </Box>

          {/* Status Feedback below slots */}
          {isFull && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ mt: 2, color: colors.emerald[400] }}
            >
              <CheckIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Trajectory Locked • Ready for Launch
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Numeric Keypad Grid */}
        <Box sx={{ maxWidth: 360, mx: "auto" }}>
          {KEYPAD_ROWS.map((row, rIdx) => (
            <Stack
              key={rIdx}
              direction="row"
              spacing={1.5}
              justifyContent="center"
              sx={{ mb: 1.5 }}
            >
              {row.map((val) => {
                const isDelete = val === "DELETE";
                const isClear = val === "CLEAR";
                const isDigit = typeof val === "number";
                const isDisabled = isDigit && isFull;

                let btnBg = colors.slate[100];
                let btnColor = colors.slate[800];
                let btnHoverBg = colors.slate[200];
                let btnBorder = `1px solid ${colors.slate[200]}`;

                if (isDelete) {
                  btnBg = colors.red[50];
                  btnColor = colors.red[600];
                  btnHoverBg = colors.red[100];
                  btnBorder = `1px solid ${colors.red[200]}`;
                } else if (isClear) {
                  btnBg = colors.slate[100];
                  btnColor = colors.slate[600];
                  btnHoverBg = colors.slate[200];
                  btnBorder = `1px solid ${colors.slate[200]}`;
                }

                return (
                  <Box
                    key={val}
                    component="button"
                    type="button"
                    onClick={() => handleKeypadPress(val)}
                    disabled={isDisabled || (isDelete && currentLength === 0)}
                    sx={{
                      flex: 1,
                      height: 52,
                      borderRadius: borderRadiusPx.lg,
                      bgcolor: btnBg,
                      color: btnColor,
                      border: btnBorder,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isDigit ? "1.25rem" : "0.85rem",
                      fontWeight: 700,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      opacity: isDisabled ? 0.45 : 1,
                      transition: "all 0.15s ease",
                      outline: "none",
                      "&:hover:not(:disabled)": {
                        bgcolor: btnHoverBg,
                        transform: "translateY(-1px)",
                      },
                      "&:active:not(:disabled)": {
                        transform: "translateY(1px)",
                      },
                    }}
                  >
                    {isDelete ? (
                      <BackspaceIcon fontSize="small" />
                    ) : isClear ? (
                      "Clear"
                    ) : (
                      val
                    )}
                  </Box>
                );
              })}
            </Stack>
          ))}
        </Box>

        {/* Informational tip */}
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: colors.slate[400],
            mt: 2,
          }}
        >
          Tip: You can also use your physical numeric keyboard to enter or delete digits.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default MissileKeypadDeck;
